import React, { useLayoutEffect, useRef, useState } from 'react';

/**
 * The leather satchel that holds a bank item grid, sized to whole rows (lane AN
 * follow-up): the frame takes the leaf's leftover height and the scroller
 * inside snaps to the tallest whole number of slot rows that fits, so the
 * bottom row is never cut in half. A bag or vault with more rows than fit
 * scrolls by whole rows' worth of content; the leaf header shows used / total.
 *
 * Geometry matches BankView's grid: 48 px `md` slots, a 6 px gap, 8 px padding.
 */
export const SATCHEL_SLOT = 48;
export const SATCHEL_GAP = 6;
export const SATCHEL_PAD = 8;

/** Height of the tallest whole-row satchel that fits `available` px (at least one row). */
export function wholeRowsHeight(available: number, slot = SATCHEL_SLOT, gap = SATCHEL_GAP, pad = SATCHEL_PAD): number {
  const rows = Math.max(1, Math.floor((available - 2 * pad + gap) / (slot + gap)));
  return 2 * pad + rows * slot + (rows - 1) * gap;
}

export interface BankSatchelProps {
  'data-testid'?: string;
  children: React.ReactNode;
}

export const BankSatchel: React.FC<BankSatchelProps> = ({ children, ...rest }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const next = wholeRowsHeight(frame.clientHeight);
      setHeight(prev => (prev === next ? prev : next));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  return <div className="bank-satchel-frame" ref={frameRef}>
    <div className="bank-satchel" data-testid={rest['data-testid']} style={height === null ? undefined : { height }}>
      {children}
    </div>
  </div>;
};
